<template lang="pug">
  .card-box.vld-parent
    Loading(v-bind:active.sync="loading" v-bind:can-cancel="false" v-bind:is-full-page="false")
    .d-flex.m-b-30
      div.mr-auto
        div.m-b-5
          small {{ $t('webp-share-report') }}
        h3.page-title.text-dark
          span {{ view.account }}
          | &raquo;
          span {{ view.property }}
          | &raquo;
          span {{ view.view }}

      div.text-right(style="white-space: nowrap")
        div {{ $t('30days' )}}
        div
          small {{ $t('100records') }}

    template(v-if="error")
      .alert.alert-danger {{ error }}
    template(v-else)
      .row.m-b-30(v-if="!loading")
        .col-md-4.text-center
          h4.header-title.text-dark.m-b-10
            | {{ $t('all') }}
          ShareChart(v-bind:share="share.all")
          div
            PercentBadge(v-bind:value="share.all.ok / (share.all.ok + share.all.ng)" classes="text-lg badge-success")
        .col-md-4.text-center
          h4.header-title.text-dark.m-b-5
            | {{ $t('mobile') }}
          span.text-muted
            | {{ $t('before-of-all') }}
            | {{ share.mobile.total | percentage(share.all.total, 1) }}
            | {{ $t('after-of-all') }}
          div(style="padding: 5px 20px")
            ShareChart(v-bind:share="share.mobile")
            div
              PercentBadge(v-bind:value="share.mobile.ok / (share.mobile.ok + share.mobile.ng)" classes="badge-success")
        .col-md-4.text-center
          h4.header-title.text-dark.m-b-5
            | {{ $t('desktop-tablet') }}
          span.text-muted
            | {{ $t('before-of-all') }}
            | {{ share.desktop.total | percentage(share.all.total, 1) }}
            | {{ $t('after-of-all') }}
          div(style="padding: 5px 20px")
            ShareChart(v-bind:share="share.desktop")
            div
              PercentBadge(v-bind:value="share.desktop.ok / (share.desktop.ok + share.desktop.ng)" classes="badge-success")

      h4.header-title.text-dark {{ $t('top5') }}
      table.table.table-striped.table-bordered.table-striped
        thead
          tr
            th
            th {{ $t('device') }}
            th {{ $t('browser') }}
            th {{ $t('version') }}
            th.text-right {{ $t('users-share') }}
        tbody
          tr(v-for="(user, i) in view.users.slice(0, view.limit)")
            td.text-right {{ i + 1 }}
            td {{ $t(user.device) }}
            td {{ user.browser }}
            td {{ user.version }}
            td.d-flex
              span.mr-auto
                span.badge.badge-success(v-if="user.webp") {{ $t('webp-ok') }}
                span.badge.badge-dark(v-else) {{ $t('webp-ng') }}
              span {{ user.users | percentage(totalUsers, 2) }}
      p(v-if="view.limit != 100")
        a(href="#" v-on:click="view.limit = 100")
          i.mdi.mdi-arrow-down.m-r-5
          | {{ $t('show-all') }}
        
</template>

<i18n>
{
  "ja": {
    "webp-share-report": "WebP対応ユーザーシェアレポート",
    "30days": "過去30日間",
    "100records": "最大100件の結果から集計",
    "top5": "デバイス・ブラウザ シェアトップ5",
    "webp-ok": "WebP対応",
    "webp-ng": "非対応",
    "device": "デバイス",
    "browser": "ブラウザ",
    "version": "バージョン",
    "users": "ユーザー数",
    "users-share": "ユーザーシェア",
    "percentage": "割合",
    "all": "全体",
    "before-of-all": "全体の",
    "after-of-all": " ",
    "show-all": "すべて表示(最大100件)",
    "desktop-tablet": "PC・タブレット",
    "desktop": "PC",
    "mobile": "スマートフォン",
    "tablet": "タブレット",
    "no-data": "このビューには対象期間のデータがありません。"
  },
  "en": {
    "webp-share-report": "WebP user share report",
    "30days": "This 30 days",
    "100records": "To to 100 records",
    "top5": "Top 5 of device and browser profile",
    "webp-ok": "WebP Available",
    "webp-ng": "Not Available",
    "device": "Device",
    "browser": "Browser",
    "version": "Version",
    "users": "Users",
    "users-share": "User Share",
    "percentage": "Percentage",
    "all": "All",
    "before-of-all": " ",
    "after-of-all": " of All",
    "show-all": "Show all (up to 100)",
    "desktop-tablet": "PC / Tablet",
    "desktop": "PC",
    "mobile": "Smartphone",
    "tablet": "Tablet",
    "no-data": "There is no data in the period."
  }
}
</i18n>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import ShareChart from '@/components/ShareChart'
import PercentBadge from '@/components/PercentBadge'
import Percentage from 'percentage'

export default {
  components: {
    Loading,
    ShareChart,
    PercentBadge,
  },
  data() {
    return {
      error: null,
      totalUsers: 0,
      loading: true,
    }
  },
  props: {
    view: Object,
  },
  filters: {
    localeString(value) {
      return parseInt(value).toLocaleString()
    },
    percentage(factor, total, digits) {
      if (total != 0) {
        return Percentage(factor / total, digits)
      } else {
        return '-'
      }
    }
  },
  computed: {
    share() {
      const byDevice = {
        desktop: { ok: 0, ng: 0 },
        mobile: { ok: 0, ng: 0 },
        all: { ok: 0, ng: 0 },
      }

      this.view.users.forEach(user => {
        const all = byDevice.all
        if (user.webp) all.ok += user.users

        const share = byDevice[user.device == 'mobile' ? 'mobile' : 'desktop']
        if (!share) return
        if (user.webp) share.ok += user.users
        else share.ng += user.users
      })

      byDevice.all.ng = this.totalUsers - byDevice.all.ok
      Object.values(byDevice).forEach(share => {
        share.total = share.ok + share.ng
      })

      return byDevice
    }
  },
  methods: {
    webpAvailable(user) {
      if (!!user.browser.match(/Android|Samsung|Chrome|Opera|Baidu/)) return true
      const version = parseInt(user.version)
      if (!!user.browser.match(/Firefox/) &&  version >= 65) return true
      if (!!user.browser.match(/Edge/) &&  version >= 18) return true
      return false
    }
  },
  async created() {
    try {
      const reportRequests = [{
        viewId: `ga:${this.view.id}`,
        dateRanges: [
          { startDate: '30daysAgo', endDate: 'today' }
        ],
        metrics: [
          { expression: 'ga:users' }
        ],
        dimensions: [
          { name: 'ga:deviceCategory' },
          { name: 'ga:browser' },
          { name: 'ga:browserVersion' },
        ],
        orderBys: [
          { fieldName: 'ga:users', sortOrder: 'DESCENDING' },
        ],
        pageSize: 100,
      }]

      const response = await window.gapi.client.request({
        path: '/v4/reports:batchGet',
        root: 'https://analyticsreporting.googleapis.com/',
        method: 'POST',
        body: {
          reportRequests,
        }
      })

      const report = response.result.reports[0]
      this.totalUsers = parseInt(report.data.totals[0].values[0])

      if (!report.data || !report.data.rows || report.data.rows.length == 0)
        throw new Error(this.$t('no-data'))

      this.view.users = report.data.rows.map(row => {
        if (!row.dimensions) return
        if (!row.metrics || !row.metrics[0]) return
        const user = {
          device: row.dimensions[0],
          browser: row.dimensions[1],
          version: row.dimensions[2],
          users: parseInt(row.metrics[0].values[0] || 0),
        }
        user.webp = this.webpAvailable(user)
        return user
      }).filter(v => !!v)
    } catch(ex) {
      this.error = ex.message
    } finally {
      this.loading = false
    }
  }
}
</script>